import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react'
import { Alert, Container, Form, Row, Button, Col, Modal } from 'react-bootstrap'
import { useNavigate } from 'react-router';
import { Applications } from '../../api/Applications';
import BdaApplicationItem from '../../components/list-items/BdaApplicationItem';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import FileSaver from 'file-saver';
import { Select } from 'antd';
import { TailSpin } from 'react-loader-spinner';
import { accessLevelOptions } from '../../utils';
import { cloneDeep } from 'lodash';
import { EventEmitter } from '../../utils/EventEmitter';
import { useCallback } from 'react';
import { BdaApplication } from '../../models/BdaApplication';
import { PagedResults } from '../../models/generics';
import AppBrowsingToolbar, { Filters } from '../../components/common/AppBrowsingToolbar';
import { DefaultOptionType } from 'antd/es/select';

const statusOptions = [
  { label: "Ready", value: "READY" },
  { label: "Starting", value: "STARTING" },
  { label: "Running", value: "RUNNING" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Stopped", value: "STOPPED" },
  { label: "Error", value: "ERROR" },
];

const bdaApplicationsListFilters: Filters = {
  'search': {
    placeholder: 'Search application...',
    query: 'freeText',
  },
  'status': {
    placeholder: 'Select status',
    select: true,
    query: 'status',
    type: 'string',
    options: statusOptions,
    customOptions: false,
  },
  'accessLevel': {
    placeholder: 'Select privacy level',
    select: true,
    query: 'accessLevel',
    type: 'string',
    options: accessLevelOptions
      .filter(option => window.__env.ALLOWED_ACCESS_LEVELS ? window.__env.ALLOWED_ACCESS_LEVELS.includes(option.value) : true),
    customOptions: true,
    renderOption: (option: DefaultOptionType, index: number) => (
      <Select.Option key={index} value={option.value} label={option.label}>
        <div>
          <FontAwesomeIcon icon={option.icon} fixedWidth />
          <span className="ms-2">{option.label}</span>
        </div>
      </Select.Option>
    )
  },
  'owned': {
    label: 'My Applications',
    checkbox: true,
    type: 'boolean',
    query: 'createdBy',
  },
  'defaultOnly': {
    label: 'Default Only',
    checkbox: true,
    type: 'boolean',
    query: 'defaultVersion',
    value: true,
  },
  'activeSchedules': {
    label: 'Active Schedules',
    checkbox: true,
    type: 'boolean',
    query: 'activeSchedule',
  },
}

export default function BdaApplicationsList() {
  const [applications, setApplications] = useState<BdaApplication[]>();
  const [error, setError] = useState(false);
  const [latestParams, setLatestParams] = useState<URLSearchParams>();

  const [totalApplications, setTotalApplications] = useState<number>();

  const [applicationToImport, setApplicationToImport] = useState<string>();
  const [applicationImportError, setApplicationImportError] = useState<string>();
  const [applicationToDelete, setApplicationToDelete] = useState<BdaApplication>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const handleDeleteModalClose = () => { setShowDeleteModal(false); setApplicationToDelete(undefined) };
  const handleImportModalClose = () => setShowImportModal(false);
  const openImportModal = () => setShowImportModal(true);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const navigate = useNavigate();

  const handleStatusMessageEvent = useCallback((evt: any) => {
    const statusMessage = evt.details;
    console.log('received message', statusMessage, applications);
    if (statusMessage.bda && !statusMessage.serviceId) {
      const index = applications!.findIndex(app => app.id === statusMessage.bda.id);
      if (index > -1) {
        const newApplications = cloneDeep(applications!);
        newApplications[index].status = statusMessage.bda.status;
        setApplications(newApplications);
      }
    }
  }, [applications]);

  useEffect(() => {
    if (applications) EventEmitter.subscribe('BDA_APP_STATUS_CHANGE', handleStatusMessageEvent)

    return () => EventEmitter.unsubscribe('BDA_APP_STATUS_CHANGE');
  }, [handleStatusMessageEvent, applications]);

  const getApplications = useCallback((queryParams?: Record<string, any>) => {
    const params = new URLSearchParams(queryParams || latestParams);
    if (queryParams) setLatestParams(params);

    Applications.getApplications(params)
      .then(
        ({ data: appPages }) => {
          setApplications((appPages as PagedResults<BdaApplication>).content);
          setTotalApplications((appPages as PagedResults<BdaApplication>).pageMetadata.totalElements);
        },
        (err) => {
          showToast('apps-error', 'Could not get applications', 'Applications Error', 'danger');
          setApplications([]);
          setError(true);
        }
      );
  }, [showToast, latestParams]);

  const reset = useCallback((params?: Record<string, any>) => {
    setError(false);
    setApplications(undefined);
    getApplications(params);
  }, [getApplications]);

  useEffect(() => {
    EventEmitter.subscribe('browsing:paramsChanged', (params: any) => {
      reset(params);
    });

    return () => EventEmitter.unsubscribe('browsing:paramsChanged');
  }, [reset]);

  const navigateToCreation = () => navigate('/bda-applications/create');
  const navigateToDetail = (app: BdaApplication) => () => navigate(`/bda-applications/${app.id}`);

  const navigateToEdit = (app: BdaApplication) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    navigate(`/bda-applications/${app.id}/edit`);
  };

  const downloadApplication = (app: BdaApplication) => async (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    showLoader();

    try {
      const { data: application } = await Applications.getApplication(app.id!);

      const appString = JSON.stringify(application, null, 2);
      const fileContent = new Blob([appString], { type: 'text/json' });
      FileSaver.saveAs(fileContent, `Alida BDA Application - ${app.name}.json`);
      hideLoader();
    } catch (e) {
      console.log(e);
      showToast('bda-export-error', 'Could not export application.', 'Application Error', 'danger');
      hideLoader();
    }

  }

  const handleApplicationFile = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const file = Array.from(evt.target.files!)[0];
    const reader = new FileReader();
    reader.readAsText(file, "UTF-8");
    reader.onload = function (e) {
      try {
        const applicationDefinition = e.target!.result as string;
        setApplicationToImport(applicationDefinition);
      }
      catch (e) {
        console.log(e);
        setApplicationImportError('Application definition file must be a valid JSON.');
      }
    }
  }

  const importApplication = () => {
    if (!applicationToImport) {
      setApplicationImportError('You must select a file for the application to import first.');
      return;
    }
    setApplicationImportError(undefined);
    localStorage.setItem('applicationToImport', applicationToImport);
    setShowImportModal(false);
    navigate('/bda-applications/create?shouldImportApp=true');
  }

  const openDeleteModal = (app: BdaApplication) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    setShowDeleteModal(true);
    setApplicationToDelete(app);
  }

  const deleteApplication = () => {
    showLoader();

    Applications.deleteApplication(applicationToDelete!.id!)
      .then(
        () => {
          showToast('bda-delete-success', 'Application deleted successfully.', 'Success', 'success');
          hideLoader();
          handleDeleteModalClose();
          reset();
        },
        () => {
          showToast('bda-delete-error', 'Could not delete application.', 'Application Error', 'danger');
          hideLoader();
        }
      );
  }

  const toggleApplication = (app: BdaApplication) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    ["RUNNING", "PARTIAL", "STARTING"].includes(app.status as string) ? stopApplication(app) : runApplication(app);
  }

  const runApplication = async (app: BdaApplication) => {
    showLoader();

    try {
      const { data: application } = await Applications.getApplication(app.id!);
      const workflowId = application.workflows[0].id!;

      Applications.runApplication(app.id!, workflowId)
        .then(
          (_) => {
            showToast('bda-run-success', 'Application started successfully.', 'Success', 'success');
            hideLoader();
          },
          (err) => {
            showToast('bda-run-error', 'Could not execute application.', 'Application Error', 'danger');
            hideLoader();
          }
        );
    } catch (e) {
      console.log(e);
      showToast('bda-run-error', 'Could not execute application.', 'Application Error', 'danger');
      hideLoader();
    }
  }

  const stopApplication = async (app: BdaApplication) => {
    showLoader();

    try {
      const { data: application } = await Applications.getApplication(app.id!);
      const workflowId = application.workflows[0].id!;

      Applications.stopApplication(app.id!, workflowId)
        .then(
          (_) => {
            showToast('bda-stop-success', 'Application stopped successfully.', 'Success', 'success');
            hideLoader();
          },
          (err) => {
            showToast('bda-stop-error', 'Could not stop application.', 'Application Error', 'danger');
            hideLoader();
          }
        );
    } catch (e) {
      console.log(e);
      showToast('bda-stop-error', 'Could not stop application.', 'Application Error', 'danger');
      hideLoader();
    }
  }

  const renderApplications = () => {
    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occured, please try again later.</span>
      </Alert>
    );

    return applications!.length === 0
      ? (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No applications found.</span>
        </Alert>
      )
      : (
        <Row>
          {
            applications!.map(app => (
              <Col xs={12} sm={6} md={4} lg={3} className="p-2" key={app.id}>
                <BdaApplicationItem
                  application={app}
                  onClick={navigateToDetail(app)}
                  onApplicationToggle={toggleApplication(app)}
                  onApplicationExport={downloadApplication(app)}
                  onApplicationEdit={navigateToEdit(app)}
                  onApplicationDelete={openDeleteModal(app)}
                />
              </Col>
            ))
          }
        </Row>
      )
  }

  const renderAsyncContent = () => {
    return applications
      ? renderApplications()
      : (
        <div className="d-flex justify-content-center py-5 my-5">
          <TailSpin
            height={100}
            width={100}
            color="#677ab1"
            radius="1"
          />
        </div>
      );
  }

  return (
    <Container className="mt-3">
      <AppBrowsingToolbar
        title="Applications List"
        actions={
          <Col xs={6} md="auto" className="text-end">
            <Button size="sm" variant="success" onClick={navigateToCreation}>
              <FontAwesomeIcon icon={solid('plus')} />
              <span className="ms-2">Create Application</span>
            </Button>
            <Button size="sm" variant="primary" className="ms-2" onClick={openImportModal}>
              <FontAwesomeIcon icon={solid('upload')} />
              <span className="ms-2">Upload Application</span>
            </Button>
          </Col>
        }
        filters={bdaApplicationsListFilters}
        total={totalApplications}
      />

      <hr />

      {renderAsyncContent()}

      <Modal show={showDeleteModal} onHide={handleDeleteModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Application</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete application <b>{applicationToDelete && applicationToDelete.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleDeleteModalClose}>
            Close
          </Button>
          <Button variant="danger" size="sm" onClick={deleteApplication}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showImportModal} onHide={handleImportModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Import Application</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="applicationFile" className="mb-3">
            <Form.Label>Choose Application file</Form.Label>
            <Form.Control type="file" size="sm" accept=".json" onChange={handleApplicationFile} />
          </Form.Group>

          {applicationImportError && <Form.Control.Feedback className="d-block" type="invalid">{applicationImportError}</Form.Control.Feedback>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" className="border" onClick={handleImportModalClose}>Close</Button>
          <Button variant="success" onClick={importApplication}>Import Application</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
